import { ExclamationTriangleIcon, NoSymbolIcon } from '@heroicons/react/24/outline'
import { ReactElement } from 'react'
import { OpsTrendGraph, type TrendSeries } from 'app/components/ai-agent-voice-ops/OpsTrendGraph'
import { SectionTitle } from 'app/components/ai-agent-voice-ops/SectionTitle'
import SimpleTable from 'app/components/table/SimpleTable'
import { TableEmptyState } from 'app/components/ui/table-empty-state'
import SegmentControl from 'app/components/viz/SegmentControl'
import { DataSourceBadge } from './DataSourceBadge'
import { ListErrorState } from './ListErrorState'
import { EMPTY_DAILY_TOTALS, type SmsDailyField } from './smsAgentOpsMetrics'
import { FAILURE_GROUP_LABEL, FAILURE_GROUPS } from './smsFailures'
import type { FailureGroupFilter, UseSmsAgentOpsReturn } from './useSmsAgentOps'

const FAILURE_SERIES: TrendSeries<SmsDailyField>[] = [
  { key: FAILURE_GROUP_LABEL.undelivered, color: `orange`, value: (totals) => totals.undeliveredTexts },
  { key: FAILURE_GROUP_LABEL.carrierFailed, color: `red`, value: (totals) => totals.carrierFailedTexts },
  { key: FAILURE_GROUP_LABEL.noConsent, color: `purple`, value: (totals) => totals.noConsentTexts },
  { key: FAILURE_GROUP_LABEL.quietHours, color: `amber`, value: (totals) => totals.quietHoursTexts },
  { key: FAILURE_GROUP_LABEL.stuckPending, color: `gray`, value: (totals) => totals.stuckPendingTexts },
]

const OPT_OUT_SERIES: TrendSeries<SmsDailyField>[] = [
  { key: `Opt-Outs`, color: `red`, value: (totals) => totals.optOuts },
]

const FAILURE_FILTER_OPTIONS: { value: FailureGroupFilter; label: string }[] = [
  { value: `all`, label: `All` },
  ...FAILURE_GROUPS.map((group) => ({ value: group, label: FAILURE_GROUP_LABEL[group] })),
]

type FailuresSectionProps = Pick<
  UseSmsAgentOpsReturn,
  | `listErrors`
  | `fromDate`
  | `toDate`
  | `isLoading`
  | `summary`
  | `daily`
  | `visibleFailures`
  | `failureCounts`
  | `failureFilter`
  | `setFailureFilter`
  | `failedTextColumns`
  | `failureListRef`
  | `optOutRows`
  | `optOutColumns`
  | `optOutListRef`
>

/** Texts that didn't go out, and patients who stopped them (plan D12, D13). */
export function FailuresSection({
  listErrors,
  fromDate,
  toDate,
  isLoading,
  summary,
  daily,
  visibleFailures,
  failureCounts,
  failureFilter,
  setFailureFilter,
  failedTextColumns,
  failureListRef,
  optOutRows,
  optOutColumns,
  optOutListRef,
}: FailuresSectionProps): ReactElement {
  return (
    <>
      <OpsTrendGraph
        title="Delivery Failures"
        adornment={<DataSourceBadge source="preview" />}
        badge={`${summary.failedTexts.toLocaleString(`en-US`)} Failed`}
        series={FAILURE_SERIES}
        daily={daily}
        emptyTotals={EMPTY_DAILY_TOTALS}
        fromDate={fromDate}
        toDate={toDate}
        isLoading={isLoading}
      />

      <div ref={failureListRef} className="mb-8 scroll-mt-4">
        <div className="mb-1 flex flex-wrap items-center justify-between gap-y-2">
          <SectionTitle
            title="Failed Texts"
            badge={`${failureCounts.all} Texts`}
            adornment={<DataSourceBadge source="live" />}
          />
          {/* Scrolls on its own on a phone rather than pushing the page sideways. */}
          <div className="max-w-full overflow-x-auto">
            <SegmentControl
              label="Failure"
              buttons={FAILURE_FILTER_OPTIONS.map((option) => ({
                label: `${option.label} ${failureCounts[option.value]}`,
                onClick: () => setFailureFilter(option.value),
                isActive: failureFilter === option.value,
              }))}
            />
          </div>
        </div>
        <p className="mb-3 text-body-sm text-subtle">Outbound texts that failed or were blocked, one row per text.</p>
        <SimpleTable
          activeSubpage="sms-agent-ops-failed-texts"
          data={visibleFailures}
          columns={failedTextColumns}
          enableDeepLinking={false}
          hideFooter
          emptyState={
            listErrors.failures ? (
              <ListErrorState subject="failed texts" />
            ) : (
              <TableEmptyState
                icon={<ExclamationTriangleIcon className="h-6 w-6" />}
                title="No failed texts"
                description="Texts that failed, were blocked, or are stuck pending show up here."
              />
            )
          }
        />
      </div>

      <OpsTrendGraph
        title="Opt-Outs"
        adornment={<DataSourceBadge source="preview" />}
        badge={`${summary.optOuts.toLocaleString(`en-US`)} Opt-Outs`}
        series={OPT_OUT_SERIES}
        daily={daily}
        emptyTotals={EMPTY_DAILY_TOTALS}
        fromDate={fromDate}
        toDate={toDate}
        isLoading={isLoading}
      />

      <div ref={optOutListRef} className="mb-8 scroll-mt-4">
        <div className="mb-1">
          <SectionTitle
            title="Opt-Out Log"
            badge={`${optOutRows?.length ?? 0} Numbers`}
            adornment={<DataSourceBadge source="live" />}
          />
        </div>
        <p className="mb-3 text-body-sm text-subtle">
          Numbers that opted out in the date range and are still opted out. A number that texted START is gone.
        </p>
        <SimpleTable
          activeSubpage="sms-agent-ops-opt-outs"
          data={optOutRows}
          columns={optOutColumns}
          enableDeepLinking={false}
          hideFooter
          emptyState={
            listErrors.optOuts ? (
              <ListErrorState subject="opt-outs" />
            ) : (
              <TableEmptyState
                icon={<NoSymbolIcon className="h-6 w-6" />}
                title="No opt-outs"
                description="Numbers that opt out by STOP, a form, or staff show up here."
              />
            )
          }
        />
      </div>
    </>
  )
}
